import { useParams, useNavigate, useSearchParams } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useCallback, useRef, useState } from "react";
import {
  RefreshCw,
  Pencil,
  AlertTriangle,
  AlertCircle,
  Camera,
  ImageOff,
  TriangleAlert,
  ChevronDown,
  ChevronUp,
  Trash2,
  QrCode,
  EyeOff,
  PauseCircle,
  Ban,
  CheckCircle2,
  XCircle,
  Loader2,
  Settings2,
} from "lucide-react";
import { downloadStoreQr } from "@/lib/utils/qr";
import { MerchantDashboardLink } from "@/components/MerchantDashboardLink";
import Compressor from "compressorjs";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import {
  getStore,
  deleteStore,
  dismissStoreRemoval,
  uploadStoreImage,
  listMenuItems,
  listStoreTargetDeals,
  listStoreBundleDeals,
  listStoreBuyXGetYDeals,
  getStampCard,
  listStampRewards,
  listPointRewards,
  getSettings,
  approveStore,
  unapproveStore,
} from "@/lib/api/admin";
import { SpinnerCenter } from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StatusBadge } from "@/components/StatusBadge";
import { cn } from "@/lib/utils";
import { formatDate, formatDateTime } from "@/lib/utils/date";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { StorePriceLimitSection } from "./components/StorePriceLimitSection";
import { StoreEventsEnabledSection } from "./components/StoreEventsEnabledSection";
import { StoreAppointmentsEnabledSection } from "./components/StoreAppointmentsEnabledSection";
import { StoreMembersSection } from "./components/StoreMembersSection";
import { MenuTab } from "./components/tabs/MenuTab";
import { TargetDealsTab } from "./components/tabs/TargetDealsTab";
import { BundleDealsTab } from "./components/tabs/BundleDealsTab";
import { MultiBuyDealsTab } from "./components/tabs/MultiBuyDealsTab";
import { PointRewardsTab } from "./components/tabs/PointRewardsTab";
import { StampRewardsTab } from "./components/tabs/StampRewardsTab";
import { EventsTab } from "./components/tabs/EventsTab";
import { AppointmentsTab } from "./components/tabs/AppointmentsTab";
import { AnalyticsTab } from "./components/tabs/AnalyticsTab";
import { ActivityTab } from "./components/tabs/ActivityTab";
import { MerchantActivityTab } from "./components/tabs/MerchantActivityTab";
import { StoreStaffTab } from "./components/tabs/StoreStaffTab";
import { BusinessHoursCard } from "./components/BusinessHoursCard";
import { StoreEditDialog } from "./components/StoreEditDialog";
import { StoreHoursDialog } from "./components/StoreHoursDialog";
import { StoreImageUploadDialog } from "./components/StoreImageUploadDialog";
// import { ReferralProgramSettings } from "./components/ReferralProgramSettings";
import {
  isStoreDetailTab,
  type StoreDetailTab,
  TAB_ANALYTICS,
  TAB_APPOINTMENTS,
  TAB_EVENTS,
  TAB_MENU,
  TAB_TARGET_DEALS,
  TAB_BUNDLE_DEALS,
  TAB_MULTI_BUY_DEALS,
  TAB_POINT_REWARDS,
  TAB_STAMP_REWARDS,
  // TAB_REFERRAL_PROGRAM,
  TAB_ACTIVITY,
  TAB_MERCHANT_ACTIVITY,
  TAB_STAFF,
} from "@/lib/utils/storeTabs";

export function StoreDetailPage() {
  const { id } = useParams<{ id: string }>();
  const user = useAdminAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const activeTab: StoreDetailTab =
    requestedTab && isStoreDetailTab(requestedTab) ? requestedTab : TAB_ANALYTICS;

  function handleTabChange(value: string) {
    if (!isStoreDetailTab(value)) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", value);
        return next;
      },
      { replace: true }
    );
  }

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState("");
  const [dangerExpanded, setDangerExpanded] = useState(false);
  const [adminControlsExpanded, setAdminControlsExpanded] = useState(false);
  const [dismissOpen, setDismissOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [unapproveOpen, setUnapproveOpen] = useState(false);
  const [hoursOpen, setHoursOpen] = useState(false);

  const coverInputRef = useRef<HTMLInputElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  // Pending image = file selected but not yet uploaded; shown in preview dialog
  const [pendingImage, setPendingImage] = useState<{
    file: File;
    type: "cover" | "logo";
    previewUrl: string;
  } | null>(null);

  async function handleDownloadQr() {
    if (!store?.short_id) return;
    await downloadStoreQr(store.short_id, store.name);
  }

  const clearPending = useCallback(() => {
    if (pendingImage) URL.revokeObjectURL(pendingImage.previewUrl);
    setPendingImage(null);
  }, [pendingImage]);

  // Compress here (not in the mutation) so mutationFn stays a plain uploadStoreImage call.
  function handleImageFile(file: File, type: "cover" | "logo") {
    const maxWidth = type === "cover" ? 1600 : 800;
    const maxHeight = type === "cover" ? 900 : 800;
    new Compressor(file, {
      maxWidth,
      maxHeight,
      quality: 0.85,
      success(result) {
        const compressed = new File([result], file.name, { type: result.type || file.type });
        setPendingImage({ file: compressed, type, previewUrl: URL.createObjectURL(compressed) });
      },
      error() {
        // Compressor failed — use the original file
        setPendingImage({ file, type, previewUrl: URL.createObjectURL(file) });
      },
    });
  }

  const uploadMutation = useMutation({
    mutationFn: ({ file, type }: { file: File; type: "cover" | "logo" }) =>
      uploadStoreImage(user!, id!, file, type),
    onSuccess: (_, { type }) => {
      void qc.invalidateQueries({ queryKey: ["store", id] });
      toast.success(type === "cover" ? "Cover image updated." : "Logo updated.");
      clearPending();
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const {
    data: store,
    isLoading,
    isError: storeError,
    refetch,
  } = useQuery({
    queryKey: ["store", id],
    queryFn: async () => getStore(user!, id!),
    enabled: !!user && !!id,
  });

  const needsVisibilityCheck = store?.status === "active" && !store.visible;

  const { data: menuItemsData, isLoading: menuItemsLoading } = useQuery({
    queryKey: ["menu-items", id],
    queryFn: () => listMenuItems(user!, id!, { limit: 100 }),
    enabled: !!user && !!id && needsVisibilityCheck,
    staleTime: 30_000,
  });

  const { data: stampCard, isLoading: stampCardLoading } = useQuery({
    queryKey: ["stamp-card", id],
    queryFn: () => getStampCard(user!, id!),
    enabled: !!user && !!id && needsVisibilityCheck,
    staleTime: 30_000,
  });

  const { data: stampRewards, isLoading: stampRewardsLoading } = useQuery({
    queryKey: ["stamp-rewards", id],
    queryFn: () => listStampRewards(user!, id!),
    enabled: !!user && !!id && needsVisibilityCheck,
    staleTime: 30_000,
  });

  const { data: pointRewards, isLoading: pointRewardsLoading } = useQuery({
    queryKey: ["point-rewards", id],
    queryFn: () => listPointRewards(user!, id!),
    enabled: !!user && !!id && needsVisibilityCheck,
    staleTime: 30_000,
  });

  // The visibility gate counts Target, Combo and Multi-buy deals together.
  const { data: targetDeals, isLoading: targetDealsLoading } = useQuery({
    queryKey: ["target-deals", id],
    queryFn: () => listStoreTargetDeals(user!, id!),
    enabled: !!user && !!id && needsVisibilityCheck,
    staleTime: 30_000,
  });

  const { data: bundleDeals, isLoading: bundleDealsLoading } = useQuery({
    queryKey: ["bundle-deals", id],
    queryFn: () => listStoreBundleDeals(user!, id!),
    enabled: !!user && !!id && needsVisibilityCheck,
    staleTime: 30_000,
  });

  const { data: buyXGetYDeals, isLoading: buyXGetYDealsLoading } = useQuery({
    queryKey: ["buy-x-get-y-deals", id],
    queryFn: () => listStoreBuyXGetYDeals(user!, id!),
    enabled: !!user && !!id && needsVisibilityCheck,
    staleTime: 30_000,
  });

  const { data: settings, isLoading: settingsLoading } = useQuery({
    queryKey: ["settings"],
    queryFn: () => getSettings(user!),
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });
  const reqs = settings?.visibility_requirements;

  const deleteMutation = useMutation({
    mutationFn: async () => deleteStore(user!, id!),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["stores"] });
      void qc.invalidateQueries({ queryKey: ["removal-requests"] });
      void qc.invalidateQueries({ queryKey: ["dashboard-summary"] });
      toast.success("Store permanently deleted.");
      void navigate("/stores");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const dismissMutation = useMutation({
    mutationFn: async () => dismissStoreRemoval(user!, id!),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["store", id] });
      void qc.invalidateQueries({ queryKey: ["stores"] });
      void qc.invalidateQueries({ queryKey: ["dashboard-summary"] });
      setDismissOpen(false);
      toast.success("Removal request dismissed.");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  function invalidateStoreQueries() {
    void qc.invalidateQueries({ queryKey: ["store", id] });
    void qc.invalidateQueries({ queryKey: ["stores"] });
    void qc.invalidateQueries({ queryKey: ["dashboard-summary"] });
  }

  const approveMutation = useMutation({
    mutationFn: async () => approveStore(user!, id!),
    onSuccess: () => {
      invalidateStoreQueries();
      setApproveOpen(false);
      toast.success("Store approved.");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const unapproveMutation = useMutation({
    mutationFn: async () => unapproveStore(user!, id!),
    onSuccess: () => {
      invalidateStoreQueries();
      setUnapproveOpen(false);
      toast.success("Store approval removed.");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  if (isLoading) return <SpinnerCenter />;

  if (storeError)
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-20 text-center">
        <AlertCircle className="size-8 text-muted-foreground/50" />
        <p className="font-medium text-muted-foreground">Failed to load store</p>
        <p className="text-sm text-muted-foreground/70">
          <button
            onClick={() => void refetch()}
            className="underline underline-offset-4 hover:text-foreground transition-colors"
          >
            Try again
          </button>
        </p>
      </div>
    );

  if (!store) return null;

  const isNightclub = store.category === "nightclub";
  const showEvents = isNightclub || store.events_enabled;
  const isBeauty = store.category === "beauty";
  const showAppointments = isBeauty || store.appointments_enabled;

  return (
    <div className="space-y-0">
      {/* ── Cover image header ── */}
      <div className="relative">
        {/* Cover — click anywhere to change */}
        <button
          type="button"
          onClick={() => coverInputRef.current?.click()}
          disabled={uploadMutation.isPending}
          className="group relative w-full rounded-xl overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed"
        >
          <div className="w-full h-40 sm:h-48 md:h-58 bg-muted">
            {store.cover_image_url ? (
              <img src={store.cover_image_url} alt="Cover" className="w-full h-full object-cover" />
            ) : (
              <div className="flex h-full items-center justify-center">
                <ImageOff className="size-10 text-muted-foreground/30" />
              </div>
            )}
          </div>
          <div className="absolute inset-0 flex items-center justify-center transition-opacity bg-black/30 opacity-0 group-hover:opacity-100">
            <div className="flex items-center gap-1.5 text-white text-sm font-medium">
              <Camera className="size-4" />
              Change cover
            </div>
          </div>
        </button>
        <input
          ref={coverInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/avif"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleImageFile(file, "cover");
            e.target.value = "";
          }}
        />

        {/* Logo — click to change */}
        <div className="absolute -bottom-8 left-4">
          <button
            type="button"
            onClick={() => logoInputRef.current?.click()}
            disabled={uploadMutation.isPending}
            className="group relative w-28 h-28 sm:w-30 sm:h-30 rounded-full border-4 border-background bg-muted overflow-hidden shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed"
          >
            {store.logo_url ? (
              <img src={store.logo_url} alt={store.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <ImageOff className="size-7 text-muted-foreground/40" />
              </div>
            )}
            <div className="absolute inset-0 flex items-center justify-center rounded-full transition-opacity bg-black/40 opacity-0 group-hover:opacity-100">
              <Camera className="size-4 text-white" />
            </div>
          </button>
          <input
            ref={logoInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/avif"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleImageFile(file, "logo");
              e.target.value = "";
            }}
          />
        </div>
      </div>

      {/* ── Name row ── */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 pt-10">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-bold wrap-break-word">{store.name}</h1>
            <StatusBadge status={store.status} visible={store.visible} />
            <MerchantDashboardLink storeId={store.id} />
          </div>
          <p className="text-sm text-muted-foreground capitalize mt-0.5">
            {store.category} · Created {formatDate(store.created_at)}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap sm:justify-end">
          {store.short_id && (
            <Button variant="outline" size="sm" onClick={() => void handleDownloadQr()}>
              <QrCode className="size-3.5 mr-1.5" />
              Download QR
            </Button>
          )}
          {store.approved_at == null && (
            <Button
              size="sm"
              onClick={() => setApproveOpen(true)}
              disabled={approveMutation.isPending}
            >
              <CheckCircle2 className="size-3.5 mr-1.5" />
              Approve
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
            <Pencil className="size-3.5 mr-1.5" />
            Edit
          </Button>
          <Button variant="outline" size="icon" className="size-8" onClick={() => void refetch()}>
            <RefreshCw className="size-4" />
          </Button>
        </div>
      </div>

      {/* ── Status banner ── */}
      {(store.status !== "active" || !store.visible) &&
        (() => {
          const isInactive = store.status === "active" && !store.visible;

          let icon: React.ReactNode;
          let title: string;
          let description: string;
          let cls: { container: string; text: string; desc: string; divider: string };

          if (store.status === "suspended") {
            icon = <Ban className="size-4 shrink-0 mt-0.5 text-orange-700" />;
            title = "Store Suspended";
            description = "This store has been suspended and is not visible to customers.";
            cls = {
              container: "bg-orange-50 border-orange-300",
              text: "text-orange-800",
              desc: "text-orange-700",
              divider: "border-orange-200",
            };
          } else if (store.status === "hidden") {
            icon = <PauseCircle className="size-4 shrink-0 mt-0.5 text-orange-500" />;
            title = "Store Hidden";
            description = "This store has been manually hidden from customers.";
            cls = {
              container: "bg-orange-50 border-orange-200",
              text: "text-orange-700",
              desc: "text-orange-600",
              divider: "border-orange-100",
            };
          } else {
            icon = <EyeOff className="size-4 shrink-0 mt-0.5 text-amber-600" />;
            title = "Store Not Visible Yet";
            description =
              "This store is active but hasn't met all requirements to become visible to customers.";
            cls = {
              container: "bg-amber-50 border-amber-200",
              text: "text-amber-700",
              desc: "text-amber-600",
              divider: "border-amber-200",
            };
          }

          type Req = { label: string; met: boolean | null; hint: string };
          const requirements: Req[] = (() => {
            if (!isInactive || !reqs || settingsLoading) return [];
            const list: Req[] = [];

            if (reqs.require_schedule) {
              list.push({
                label: "Business hours",
                met: !!store.schedule && Object.values(store.schedule).some((s) => s.length > 0),
                hint: "Configure at least one open time slot",
              });
            }

            if (reqs.min_active_deals > 0) {
              const n = reqs.min_active_deals;
              const dealsLoading =
                targetDealsLoading || bundleDealsLoading || buyXGetYDealsLoading;
              const count =
                (targetDeals?.filter((d) => d.status === "active").length ?? 0) +
                (bundleDeals?.filter((d) => d.status === "active").length ?? 0) +
                (buyXGetYDeals?.filter((d) => d.status === "active").length ?? 0);
              list.push({
                label: n === 1 ? "Active deal" : `${n} active deals`,
                met: dealsLoading ? null : count >= n,
                hint:
                  n === 1
                    ? "At least one active Target, Combo or Multi-buy deal is required"
                    : `At least ${n} active Target, Combo or Multi-buy deals are required`,
              });
            }

            if (reqs.min_any_menu_items > 0) {
              const n = reqs.min_any_menu_items;
              const count = menuItemsData?.data.filter((i) => i.status === "active").length ?? 0;
              list.push({
                label: n === 1 ? "Menu item" : `${n} menu items`,
                met: menuItemsLoading ? null : count >= n,
                hint:
                  n === 1
                    ? "At least one active menu item is required"
                    : `At least ${n} active menu items are required`,
              });
            }

            if (reqs.min_stamp_rewards > 0) {
              const n = reqs.min_stamp_rewards;
              const cardEnabled = stampCard?.enabled ?? false;
              const count = cardEnabled
                ? (stampRewards?.filter((r) => r.status === "active").length ?? 0)
                : 0;
              list.push({
                label: n === 1 ? "Stamp card & reward" : `${n} stamp rewards`,
                met: stampCardLoading || stampRewardsLoading ? null : count >= n,
                hint:
                  n === 1
                    ? "Enable the stamp card with at least one active reward"
                    : `Enable the stamp card with at least ${n} active rewards`,
              });
            }

            if (reqs.min_point_rewards > 0) {
              const n = reqs.min_point_rewards;
              const count = pointRewards?.filter((r) => r.status === "active").length ?? 0;
              list.push({
                label: n === 1 ? "Point reward" : `${n} point rewards`,
                met: pointRewardsLoading ? null : count >= n,
                hint:
                  n === 1
                    ? "Add at least one active point reward"
                    : `Add at least ${n} active point rewards`,
              });
            }

            if (reqs.require_approval) {
              list.push({
                label: "Store approved",
                met: store.approved_at != null,
                hint: "Admin approval is required before the store becomes visible",
              });
            }

            return list;
          })();

          return (
            <div className={`rounded-lg border px-4 py-3 mt-4 ${cls.container}`}>
              <div className="flex items-start gap-3">
                {icon}
                <div className="min-w-0">
                  <p className={`text-sm font-semibold ${cls.text}`}>{title}</p>
                  <p className={`text-sm ${cls.desc}`}>{description}</p>
                </div>
              </div>
              {requirements.length > 0 && (
                <div
                  className={`mt-3 pt-3 border-t grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 ${cls.divider}`}
                >
                  {requirements.map((req) => (
                    <div key={req.label} className="flex items-start gap-2">
                      {req.met === null ? (
                        <Loader2 className="size-4 shrink-0 mt-0.5 text-muted-foreground animate-spin" />
                      ) : req.met ? (
                        <CheckCircle2 className="size-4 shrink-0 mt-0.5 text-green-600" />
                      ) : (
                        <XCircle className="size-4 shrink-0 mt-0.5 text-destructive" />
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-medium leading-tight">{req.label}</p>
                        {!req.met && req.hint && (
                          <p className="text-xs text-muted-foreground">{req.hint}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

      {/* ── Main grid ── */}
      <div className="grid gap-6 lg:grid-cols-[1fr_2fr] mt-6">
        {/* ─ Left column ─ */}
        <div className="space-y-4 min-w-0">
          {/* Store info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Store Info</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {store.description && <p className="text-muted-foreground">{store.description}</p>}
              <div>
                <p className="text-xs text-muted-foreground uppercase">Category</p>
                <p className="capitalize">{store.category}</p>
              </div>
              {(store.address_line1 || store.city || store.province) && (
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Address</p>
                  <p>
                    {[
                      store.address_line1,
                      store.address_line2,
                      store.city,
                      store.province,
                      store.country,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                  {store.postal_code && (
                    <p className="text-muted-foreground">{store.postal_code}</p>
                  )}
                </div>
              )}
              {store.phone && (
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Phone</p>
                  <p>{store.phone}</p>
                </div>
              )}
              {store.email && (
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Email</p>
                  <p>{store.email}</p>
                </div>
              )}
              {store.website && (
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Website</p>
                  <a
                    href={store.website}
                    className="text-primary hover:underline break-all"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {store.website}
                  </a>
                </div>
              )}
              {store.short_id && (
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Short ID</p>
                  <p className="font-mono">{store.short_id}</p>
                </div>
              )}
              <div>
                <p className="text-xs text-muted-foreground uppercase">Store ID</p>
                <p className="font-mono text-xs break-all">{store.id}</p>
              </div>
              {store.table_count != null && (
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Tables</p>
                  <p>{store.table_count}</p>
                </div>
              )}
              {store.approved_at != null && (
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Approved On</p>
                  <p>{formatDateTime(store.approved_at)}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Public contact */}
          {(store.public_phone || store.public_email) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Public Contact</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {store.public_phone && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Phone</p>
                    <p>{store.public_phone}</p>
                  </div>
                )}
                {store.public_email && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Email</p>
                    <p>{store.public_email}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Business hours */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-base">Business Hours</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={() => setHoursOpen(true)}
              >
                <Pencil className="size-3 mr-1" />
                Edit
              </Button>
            </CardHeader>
            <CardContent>
              {store.schedule && Object.keys(store.schedule).length > 0 ? (
                <BusinessHoursCard schedule={store.schedule} />
              ) : (
                <p className="text-sm text-muted-foreground">No hours set.</p>
              )}
            </CardContent>
          </Card>

          {/* Members */}
          <StoreMembersSection storeId={store.id} canManageAppointments={showAppointments} />

          {/* Admin Controls — accordion, groups store-level admin overrides */}
          <div
            className={cn(
              "rounded-lg border-[1.5px] transition-colors",
              adminControlsExpanded ? "border-primary/40" : "border-border"
            )}
          >
            <button
              className="flex w-full items-center gap-2 p-4 text-left"
              onClick={() => setAdminControlsExpanded((v) => !v)}
            >
              <Settings2 className="size-5 shrink-0 text-muted-foreground" />
              <span className="flex-1 text-sm font-semibold">Admin Controls</span>
              {adminControlsExpanded ? (
                <ChevronUp className="size-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="size-4 text-muted-foreground" />
              )}
            </button>
            {adminControlsExpanded && (
              <>
                <div className="h-px bg-border" />
                <div className="space-y-4 p-4">
                  <StorePriceLimitSection store={store} />
                  <StoreEventsEnabledSection store={store} />
                  <StoreAppointmentsEnabledSection store={store} />
                  {store.approved_at != null && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-destructive"
                      onClick={() => setUnapproveOpen(true)}
                      disabled={unapproveMutation.isPending}
                    >
                      Unapprove Store
                    </Button>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Removal request */}
          {store.removal_requested_at && (
            <Card className="border-orange-200 dark:border-orange-800">
              <CardContent className="pt-4 space-y-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="size-4 text-orange-600 dark:text-orange-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-orange-700 dark:text-orange-300">
                      Removal Requested
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatDateTime(store.removal_requested_at)}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setDismissOpen(true)}>
                    Dismiss Request
                  </Button>
                  <Button variant="destructive" size="sm" onClick={() => setDeleteOpen(true)}>
                    Delete Store
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Danger zone — accordion, only when no removal request */}
          {!store.removal_requested_at && (
            <div
              className={cn(
                "rounded-lg border-[1.5px] transition-colors",
                dangerExpanded ? "border-destructive/60" : "border-destructive/30"
              )}
            >
              <button
                className="flex w-full items-center gap-2 p-4 text-left"
                onClick={() => setDangerExpanded((v) => !v)}
              >
                <TriangleAlert className="size-5 shrink-0 text-destructive" />
                <span className="flex-1 text-sm font-semibold text-destructive">Danger Zone</span>
                {dangerExpanded ? (
                  <ChevronUp className="size-4 text-destructive" />
                ) : (
                  <ChevronDown className="size-4 text-destructive" />
                )}
              </button>
              {dangerExpanded && (
                <>
                  <div className="h-px bg-destructive/30" />
                  <div className="space-y-3 p-4">
                    <p className="text-sm font-semibold text-destructive">Delete Store</p>
                    <p className="text-sm text-muted-foreground">
                      Permanently delete this store and all associated data including discounts,
                      rewards, stamp cards, events, and members. This action cannot be undone.
                    </p>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setDeleteOpen(true)}
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="size-4 mr-1" /> Delete Store
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* ─ Right column: tabs ─ */}
        <Tabs
          value={
            (activeTab === TAB_APPOINTMENTS && !showAppointments) ||
            (activeTab === TAB_EVENTS && !showEvents)
              ? TAB_ANALYTICS
              : activeTab
          }
          onValueChange={handleTabChange}
          className="min-w-0"
        >
          <TabsList className="min-w-0">
            <TabsTrigger value={TAB_ANALYTICS}>Analytics</TabsTrigger>
            {showAppointments && <TabsTrigger value={TAB_APPOINTMENTS}>Appointments</TabsTrigger>}
            {showEvents && <TabsTrigger value={TAB_EVENTS}>Events</TabsTrigger>}
            <TabsTrigger value={TAB_MENU}>Menu</TabsTrigger>
            <TabsTrigger value={TAB_TARGET_DEALS}>Target Deals</TabsTrigger>
            <TabsTrigger value={TAB_BUNDLE_DEALS}>Combos</TabsTrigger>
            <TabsTrigger value={TAB_MULTI_BUY_DEALS}>Multi Buy</TabsTrigger>
            <TabsTrigger value={TAB_POINT_REWARDS}>Point Rewards</TabsTrigger>
            <TabsTrigger value={TAB_STAMP_REWARDS}>Stamp Rewards</TabsTrigger>
            {/* Referral Program temporarily disabled: <TabsTrigger value={TAB_REFERRAL_PROGRAM}>Referral Program</TabsTrigger> */}
            <TabsTrigger value={TAB_ACTIVITY}>User Activity</TabsTrigger>
            <TabsTrigger value={TAB_MERCHANT_ACTIVITY}>Store Activity</TabsTrigger>
            <TabsTrigger value={TAB_STAFF}>Staff</TabsTrigger>
          </TabsList>

          <TabsContent value={TAB_ANALYTICS} className="mt-4">
            <AnalyticsTab storeId={store.id} />
          </TabsContent>

          {showAppointments && (
            <TabsContent value={TAB_APPOINTMENTS} className="mt-4">
              <AppointmentsTab
                storeId={store.id}
                storeCategory={store.category}
                priceLimitBypass={store.price_limit_bypass}
              />
            </TabsContent>
          )}

          {showEvents && (
            <TabsContent value={TAB_EVENTS} className="mt-4">
              <EventsTab
                storeId={store.id}
                priceLimitBypass={store.price_limit_bypass}
                storeCategory={store.category}
                storeSlug={store.slug}
              />
            </TabsContent>
          )}

          <TabsContent value={TAB_MENU} className="mt-4">
            <MenuTab
              storeId={store.id}
              storeCategory={store.category}
              priceLimitBypass={store.price_limit_bypass}
            />
          </TabsContent>

          <TabsContent value={TAB_TARGET_DEALS} className="mt-4">
            <TargetDealsTab storeId={store.id} canManageAppointments={showAppointments} />
          </TabsContent>

          <TabsContent value={TAB_BUNDLE_DEALS} className="mt-4">
            <BundleDealsTab storeId={store.id} />
          </TabsContent>

          <TabsContent value={TAB_MULTI_BUY_DEALS} className="mt-4">
            <MultiBuyDealsTab storeId={store.id} />
          </TabsContent>

          <TabsContent value={TAB_POINT_REWARDS} className="mt-4">
            <PointRewardsTab
              storeId={store.id}
              priceLimitBypass={store.price_limit_bypass}
              storeCategory={store.category}
            />
          </TabsContent>

          <TabsContent value={TAB_STAMP_REWARDS} className="mt-4">
            <StampRewardsTab
              storeId={store.id}
              priceLimitBypass={store.price_limit_bypass}
              storeCategory={store.category}
            />
          </TabsContent>

          {/* Referral Program temporarily disabled
          <TabsContent value={TAB_REFERRAL_PROGRAM} className="mt-4">
            <ReferralProgramSettings storeId={store.id} />
          </TabsContent>
          */}

          <TabsContent value={TAB_ACTIVITY} className="mt-4">
            <ActivityTab storeId={store.id} />
          </TabsContent>

          <TabsContent value={TAB_MERCHANT_ACTIVITY} className="mt-4">
            <MerchantActivityTab storeId={store.id} />
          </TabsContent>

          <TabsContent value={TAB_STAFF} className="mt-4">
            <StoreStaffTab storeId={store.id} />
          </TabsContent>
        </Tabs>
      </div>

      {/* ── Image preview / confirm dialog ── */}
      <StoreImageUploadDialog
        pendingImage={pendingImage}
        isPending={uploadMutation.isPending}
        onCancel={clearPending}
        onUpload={(file, type) => uploadMutation.mutate({ file, type })}
      />

      {/* ── Edit dialog ── */}
      <StoreEditDialog store={store} open={editOpen} onOpenChange={setEditOpen} />

      {/* ── Hours dialog ── */}
      <StoreHoursDialog store={store} open={hoursOpen} onOpenChange={setHoursOpen} />

      {/* ── Approve dialog ── */}
      <ConfirmDialog
        open={approveOpen}
        onOpenChange={setApproveOpen}
        title="Approve store"
        description={`Approve "${store.name}"? Once all other requirements are met, the store will become publicly visible.`}
        confirmLabel="Approve"
        isPending={approveMutation.isPending}
        onConfirm={() => approveMutation.mutate()}
      />

      {/* ── Unapprove dialog ── */}
      <ConfirmDialog
        open={unapproveOpen}
        onOpenChange={setUnapproveOpen}
        title="Remove store approval"
        description={`Remove the approval of "${store.name}"? The store will no longer be publicly visible until it is approved again.`}
        confirmLabel="Unapprove"
        destructive
        isPending={unapproveMutation.isPending}
        onConfirm={() => unapproveMutation.mutate()}
      />

      {/* ── Dismiss removal dialog ── */}
      <ConfirmDialog
        open={dismissOpen}
        onOpenChange={setDismissOpen}
        title="Dismiss removal request"
        description={`Dismiss the removal request for "${store.name}"? The store will remain active.`}
        confirmLabel="Dismiss"
        isPending={dismissMutation.isPending}
        onConfirm={() => dismissMutation.mutate()}
      />

      {/* ── Delete dialog — requires typing store name ── */}
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteOpen(false);
            setDeleteConfirmName("");
          }
        }}
        title="Permanently delete store"
        description={`Type the store name "${store.name}" below to confirm deletion. This cannot be undone.`}
        confirmLabel="Delete Permanently"
        destructive
        isPending={deleteMutation.isPending}
        confirmInput={{
          value: deleteConfirmName,
          onChange: setDeleteConfirmName,
          placeholder: store.name,
          requiredValue: store.name,
        }}
        onConfirm={() => deleteMutation.mutate()}
      />
    </div>
  );
}
